<template>
  <div class="select-content">
    <div class="container">
      <div class="select-base">
        <Header class="header--select">
          <template #title>背景設定</template>
          <template #menu>
            <EnterButton
              class="header-btn btn-complete"
              :class="{ 'disabled-button': !canClickDecisionBtn }"
              @click="onClickDecisionBtn"
            >
              決定
            </EnterButton>
          </template>
        </Header>
        <!-- 背景画像種別選択 -->
        <div class="select-base__baseillust-type">
          <div id="baseillust-type-list-1" class="baseillust-type-list">
            <button
              id="baseillust-type-list__item-1"
              class="baseillust-type-list__item"
              value="1"
              @click="toggleBaseillustType(1)"
            >
              <i class="far fa-image baseillust-type-icon"></i>
            </button>
            <button
              id="baseillust-type-list__item-2"
              class="baseillust-type-list__item"
              value="2"
              @click="toggleBaseillustType(2)"
            >
              <img
                alt="背景"
                src="../../public/assets/img/base_illust/thumb/japanese-paper_white.jpg"
              />
            </button>
          </div>
        </div>
        <!-- ベース画像選択 -->
        <div class="select-base__illust">
          <div id="baseillust-container-1" class="baseillust-container">
            <Upload
              :uploaded-image="uploadedImage"
              @upload-image="uploadImage"
              @remove-image="removeUploadedImage"
            />
          </div>
          <div id="baseillust-container-2" class="baseillust-container">
            <button
              v-for="baseillust in baseillusts"
              :key="baseillust.id"
              class="baseillust"
              :class="{ 'is-select': isImagePreselected(baseillust) }"
              :value="baseillust.id"
              @click="onClickBaseillust(baseillust)"
            >
              <img
                :src="`/app/assets/img/base_illust/thumb/${baseillust.fileName}`"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 保存データ読み込みアラートダイアログ -->
    <div id="alert-load" class="alert-container is-hidden">
      <Alert
        :ng-text="'はじめから'"
        :on-click-ng="onClickRestart"
        :ok-text="'続きから'"
        :on-click-ok="onClickContinue"
      >
        セーブされたデータがあります。<br />続きから作成しますか？
      </Alert>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useGlobalStore } from "@/stores/global";
import BaseillustMaster from "@/masters/baseillust-master";
import Select from "@/javascripts/image/select";
import EnterButton from "@/components/EnterButton.vue";
import Header from "@/components/Header.vue";
import Alert from "@/components/Alert.vue";
import Upload from "@/components/Upload.vue";

/**
 * TODO
 *  ・lazyload
 *    ・現状、imgの画像の指定をdata-srcからsrcに変更してしまっている
 */
export default defineComponent({
  name: "Select",
  components: {
    EnterButton,
    Header,
    Alert,
    Upload,
  },
  setup() {
    const router = useRouter();
    const toTrim = () => router.push({ name: "trim" });
    const toEdit = () => router.push({ name: "edit" });

    const select = new Select();

    const { imageEditor } = useGlobalStore();
    const isShowAlertLoad: boolean = imageEditor.hasSelectedImage();

    const baseillusts = BaseillustMaster.records;
    const defaultBaseillustCategoryId: number = 1;

    /**
     * 仮選択画像
     */
    const preSelectedImage = ref<BaseillustMaster>(null);

    const uploadedImage = ref<String>(null);

    const canClickDecisionBtn = ref<Boolean>(false);

    onMounted(() => {
      setupView();
    });

    onUnmounted(() => {});

    /**
     * 画像が選択されているか
     */
    const isImagePreselected = computed(
      () =>
        function (image: BaseillustMaster): Boolean {
          if (!preSelectedImage.value) {
            return false;
          }
          return image.id == preSelectedImage.value.id;
        }
    );

    /**
     * 画面表示の初期処理
     */
    function setupView() {
      select.toggleBaseillustCategory(defaultBaseillustCategoryId);

      if (isShowAlertLoad) {
        select.showAlertLoad();
      }
    }

    /**
     * 決定ボタン押下時の処理
     */
    function onClickDecisionBtn() {
      imageEditor.selectImage(preSelectedImage.value);
      select.barragePreventAll();
      toTrim();
    }

    /**
     * 保存データ初期化ボタン押下時の処理
     */
    function onClickRestart() {
      imageEditor.resetImage();
      select.restartOnAlertLoad();
    }

    /**
     * 保存データ編集続行ボタン押下時の処理
     */
    function onClickContinue() {
      select.continueOnAlertLoad();
      toEdit();
    }

    /**
     * ベース画像押下時の処理
     */
    function onClickBaseillust(image: BaseillustMaster) {
      preselectImage(image);
      removeUploadedImage();
      makeDecisionBtnClickable();
    }

    /**
     * 画像の仮選択
     */
    function preselectImage(image: BaseillustMaster) {
      preSelectedImage.value = image;
    }

    /**
     * 背景素材種別の切り替え
     */
    function toggleBaseillustType(type: Number) {
      select.toggleBaseillustType(type);
    }

    /**
     * 画像アップロード
     */
    function uploadImage(newImage: String) {
      uploadedImage.value = newImage;
      const image: BaseillustMaster = new BaseillustMaster({
        id: BaseillustMaster.UploadedId,
        fileName: "",
        src: newImage,
      });
      preselectImage(image);
      makeDecisionBtnClickable();
    }

    /**
     * アップロード画像の削除
     */
    function removeUploadedImage() {
      uploadedImage.value = null;
      makeDecisionBtnUnclickable();
    }

    /**
     * 決定ボタンをクリック可に変更
     */
    function makeDecisionBtnClickable() {
      canClickDecisionBtn.value = true;
    }

    /**
     * 決定ボタンをクリック不可に変更
     */
    function makeDecisionBtnUnclickable() {
      canClickDecisionBtn.value = false;
    }

    return {
      toTrim,
      baseillusts,
      onClickBaseillust,
      onClickDecisionBtn,
      onClickRestart,
      onClickContinue,
      isImagePreselected,
      toggleBaseillustType,
      uploadImage,
      removeUploadedImage,
      uploadedImage,
      canClickDecisionBtn,
    };
  },
});
</script>

<style scoped>
@import "/app/assets/css/style.css";

.header--select {
  height: 160px;
}
.disabled-button {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.65;
  filter: alpha(opacity=65);
  -webkit-box-shadow: none;
  box-shadow: none;
}
.baseillust.is-select {
  border: solid 4px #fc8350;
  background: #ffffff;
  background-image: linear-gradient(45deg, #eeeeee 25%, transparent 0),
    linear-gradient(45deg, transparent 75%, #eeeeee 0),
    linear-gradient(45deg, #eeeeee 25%, transparent 0),
    linear-gradient(45deg, transparent 75%, #eeeeee 0);
  background-size: 30px 30px;
  background-position: 0 0, 15px 15px, 15px 15px, 30px 30px;
}
.baseillust.is-select:before {
  left: 10px;
}
.baseillust.is-select:after {
  right: 10px;
}
.dialog-btn {
  margin: 0 3rem;
}
.baseillust-type-icon {
  font-size: 6rem;
}
</style>
